import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Music, Sparkles, Plus, Play, Pause, Loader2, Trash2, Zap, Heart, Globe, Radio, ArrowRight, Clock, Shuffle } from "lucide-react";
import { SpotifySearch } from "./SpotifySearch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { useAudioPlayer } from "@/contexts/AudioPlayerContext";
import { useAudioPreloader } from "@/hooks/useAudioPreloader";
import { MiniAudioPlayer } from "./MiniAudioPlayer";
export interface Track {
  id: string;
  name: string;
  artist: string;
  album: string;
  imageUrl: string;
  previewUrl: string | null;
  spotifyUrl: string;
  uri: string;
  durationMs?: number;
}
interface PlaylistBuilderProps {
  onAddSong: (song: Track, eventIndex?: number) => void;
  playingSongId: string | null;
  onPlayToggle: (track: Track) => void;
  audioConsented: boolean;
  eventContext?: {
    events?: Array<{
      occasion: string;
      vibe: string;
      guestCount: number;
      venueAddress: string;
      date: any;
      startTime: string;
      endTime: string;
      loadInTime?: string;
    }>;
    occasion?: string;
    vibe?: string;
    guestCount?: number;
    venueAddress?: string;
    season?: string;
    culturalNotes?: string;
    eventDate?: string;
    startTime?: string;
    endTime?: string;
  };
  onVibesUpdate?: (vibes: string[]) => void;
  selectedGenres?: string[];
  requireVibes?: boolean;
  shouldGlow?: boolean;
  eventIndex?: number;
  onTrackCountChange?: (count: number, eventIndex: number) => void;
}
export const PlaylistBuilder = ({
  onAddSong,
  playingSongId,
  onPlayToggle,
  audioConsented,
  eventContext,
  onVibesUpdate,
  selectedGenres = [],
  requireVibes = false,
  shouldGlow = false,
  eventIndex = 0,
  onTrackCountChange,
}: PlaylistBuilderProps) => {
  const {
    toast
  } = useToast();
  const {
    currentTrack,
    isPlaying,
    isAISuggestion,
    currentTime,
    duration,
    progress,
    playbackMode,
    onAddToSelections,
    addToQueue,
    enableAutoPlay,
    isAutoPlayEnabled,
    clearQueue,
    togglePlayPause,
    stopPlayback
  } = useAudioPlayer();
  const [playlist, setPlaylist] = useState<Track[]>([]);
  const [suggestions, setSuggestions] = useState<Track[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [selectedVibes, setSelectedVibes] = useState<string[]>([]);
  const [detectedVibes, setDetectedVibes] = useState<string[]>([]);
  const [isDetectingVibes, setIsDetectingVibes] = useState(false);
  const [recentlyAppliedVibes, setRecentlyAppliedVibes] = useState<string[]>([]);
  const [genreBlend, setGenreBlend] = useState<any>(null);
  const [isClassifyingGenres, setIsClassifyingGenres] = useState(false);
  const [playlistUrl, setPlaylistUrl] = useState("");
  const [isImportingPlaylist, setIsImportingPlaylist] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [isSpotifyConnected, setIsSpotifyConnected] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [currentSurpriseSong, setCurrentSurpriseSong] = useState<Track | null>(null);
  const [hasClassifiedGenreForEvent, setHasClassifiedGenreForEvent] = useState<Set<number>>(new Set());
  const [importedPlaylistIds, setImportedPlaylistIds] = useState<Set<string>>(new Set());

  // Use eventIndex prop instead of local state to ensure sync with parent
  const selectedEventIndex = eventIndex;
  
  // Preload audio for playlist tracks for instant playback
  useAudioPreloader(playlist, 15);
  
  // Preload audio for suggestions for instant playback
  useAudioPreloader(suggestions, 10);

  // Clear surprise song when playback stops or changes
  useEffect(() => {
    if (currentSurpriseSong && playingSongId !== currentSurpriseSong.id) {
      setCurrentSurpriseSong(null);
    }
  }, [playingSongId, currentSurpriseSong]);

  // Initialize or retrieve session ID and load imported playlists
  const [sessionId, setSessionId] = useState<string>("");
  useEffect(() => {
    const existing = sessionStorage.getItem('session_id');
    if (existing) {
      setSessionId(existing);
      // Load previously imported playlist IDs from database
      loadImportedPlaylistIds(existing);
    } else {
      const newId = crypto.randomUUID();
      sessionStorage.setItem('session_id', newId);
      setSessionId(newId);
    }
  }, []);

  // Load imported playlist IDs from database to prevent re-imports
  const loadImportedPlaylistIds = async (sessId: string) => {
    try {
      const { data, error } = await supabase
        .from('favorite_songs')
        .select('spotify_uri')
        .eq('session_id', sessId)
        .eq('event_index', selectedEventIndex)
        .eq('manually_added', false);
      
      if (error) throw error;
      
      if (data) {
        const playlistIds = new Set<string>();
        data.forEach(song => {
          // Extract playlist ID from metadata if stored
          // For now, we'll track based on URIs
          if (song.spotify_uri) {
            playlistIds.add(song.spotify_uri);
          }
        });
        setImportedPlaylistIds(playlistIds);
      }
    } catch (error) {
      console.error('Error loading imported playlist IDs:', error);
    }
  };

  // Reset component state when switching between events to ensure each event starts fresh
  useEffect(() => {
    // Clear suggestions and vibes for new event
    setSuggestions([]);
    setDetectedVibes([]);
    setSelectedVibes([]);
    setGenreBlend(null);
    setIsLoadingSuggestions(false);
    setIsDetectingVibes(false);
    setIsClassifyingGenres(false);
    setRecentlyAppliedVibes([]);
    // Clear playlist to prevent stale data
    setPlaylist([]);
  }, [selectedEventIndex]);

  // Check Spotify connection status on mount
  useEffect(() => {
    const connected = sessionStorage.getItem('spotify_connected') === 'true';
    const expiresAt = sessionStorage.getItem('spotify_expires_at');
    const justConnected = sessionStorage.getItem('spotify_just_connected') === 'true';
    if (connected && expiresAt) {
      const expiry = new Date(expiresAt);
      if (expiry > new Date()) {
        setIsSpotifyConnected(true);

        // Show success toast if just returned from OAuth
        if (justConnected) {
          toast({
            title: "Spotify Connected!",
            description: "You can now import your playlists below"
          });
          sessionStorage.removeItem('spotify_just_connected');
        }
      } else {
        sessionStorage.removeItem('spotify_connected');
        sessionStorage.removeItem('spotify_expires_at');
      }
    }
  }, [toast]);
  const REQUIRED_TRACKS = 5; // Minimum 5 songs required
  const MAX_SONGS = 25; // Maximum 25 songs allowed

  const getTotalDuration = () => {
    return playlist.reduce((total, track) => total + (track.durationMs || 0), 0);
  };

  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${minutes}m`;
  };

  const totalDuration = getTotalDuration();
  const progressPercent = Math.min((playlist.length / REQUIRED_TRACKS) * 100, 100);
  const remainingTracks = Math.max(REQUIRED_TRACKS - playlist.length, 0);
  const isTargetReached = playlist.length >= REQUIRED_TRACKS;
  const isSongLimitReached = playlist.length >= MAX_SONGS;
  useEffect(() => {
    if (sessionId) {
      loadPlaylist();
    }
    // Debug logging
    console.log('PlaylistBuilder audioConsented:', audioConsented);
  }, [sessionId, selectedEventIndex]);

  // Notify parent when track count changes
  useEffect(() => {
    if (onTrackCountChange) {
      onTrackCountChange(playlist.length, selectedEventIndex);
    }
  }, [playlist.length, selectedEventIndex, onTrackCountChange]);

  // Real-time subscription for playlist changes
  useEffect(() => {
    if (!sessionId) return;
    const channel = supabase.channel(`playlist-changes-${selectedEventIndex}`).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'favorite_songs',
      filter: `session_id=eq.${sessionId}`
    }, payload => {
      console.log('Real-time playlist update:', payload);

      // For DELETE events, payload.old might not have all columns without REPLICA IDENTITY FULL
      // So reload on any event for this session that matches our event index
      if (payload.eventType === 'DELETE') {
        // For deletes, always reload since we filter by session already
        loadPlaylist();
      } else if (payload.new && (payload.new as any).event_index === selectedEventIndex) {
        // For INSERT/UPDATE, check if it's for our event
        loadPlaylist();
      }
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, selectedEventIndex]);

  // Generate initial suggestions only after user adds first song or connects to Spotify
  useEffect(() => {
    const specificEventContext = eventContext?.events?.[selectedEventIndex] || eventContext;
    const hasUserEngaged = playlist.length > 0 || isSpotifyConnected;
    if (specificEventContext && playlist.length === 0 && suggestions.length === 0 && !isLoadingSuggestions && hasUserEngaged) {
      generateInitialSuggestions(specificEventContext);
    }
  }, [eventContext, selectedEventIndex, playlist.length, isSpotifyConnected]);

  // Auto-generate suggestions when conditions are met (genres + vibes + songs)
  useEffect(() => {
    const hasInitialSelections = selectedGenres.length > 0 && selectedVibes.length > 0 && playlist.length > 0;
    const shouldGenerate = hasInitialSelections && suggestions.length === 0 && !isLoadingSuggestions;
    if (shouldGenerate) {
      console.log('Auto-generating suggestions: all initial selections made');
      const timer = setTimeout(() => {
        generateSuggestions(playlist);
      }, 1000); // Small delay to avoid rapid fire
      return () => clearTimeout(timer);
    }
  }, [selectedGenres.length, selectedVibes.length, playlist.length, suggestions.length, isLoadingSuggestions]);
  // Save playlist data to session
  const savePlaylistDataToSession = async (vibes: string[], genreBlendData?: any) => {
    if (!sessionId) return;
    try {
      const playlistData = {
        detectedVibes: vibes,
        genreBlend: genreBlendData,
        eventIndex: selectedEventIndex,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error updating session:', error);
    }
  };
  const detectPlaylistVibes = async (tracks: Track[]) => {
    // Vibe detection removed - users can manually select vibes
    if (tracks.length === 0) {
      setDetectedVibes([]);
      setSelectedVibes([]);
      return;
    }
    setIsDetectingVibes(true);
    try {
      // Skip AI vibe detection
      setDetectedVibes([]);
    } catch (error) {
      console.error('Error in vibe detection flow:', error);
    } finally {
      setIsDetectingVibes(false);
    }
  };
  const loadPlaylist = async () => {
    if (!sessionId) {
      console.warn('⚠️ No session ID available for loading playlist');
      return;
    }
    
    console.log('📂 Loading playlist for session:', sessionId, 'event:', selectedEventIndex);
    
    try {
      const {
        data,
        error
      } = await supabase.from('favorite_songs').select('*').eq('session_id', sessionId).eq('event_index', selectedEventIndex).order('created_at', {
        ascending: false
      });
      if (error) {
        console.error('❌ Error loading playlist:', error);
        throw error;
      }
      if (data) {
        console.log(`✅ Loaded ${data.length} songs from database`);
        const tracks: Track[] = data.map(song => {
          // Extract track ID from spotify URI (format: spotify:track:ID) or use the ID directly
          const trackId = song.spotify_uri ? song.spotify_uri.split(':').pop() || song.id : song.id;
          if (!trackId) {
            console.warn('Invalid spotify_uri or missing ID for song:', song.song_title);
          }
          return {
            id: trackId,
            name: song.song_title,
            artist: song.artist,
            album: song.album || '',
            imageUrl: song.image_url || '',
            previewUrl: song.preview_url || null,
            spotifyUrl: song.spotify_uri ? `https://open.spotify.com/track/${trackId}` : '',
            uri: song.spotify_uri || '',
            durationMs: song.duration_ms || 0
          };
        }).filter(track => track.id); // Filter out tracks without valid IDs

        setPlaylist(tracks);

        // Detect vibes and auto-generate suggestions if we have songs
        if (tracks.length > 0) {
          detectPlaylistVibes(tracks);
          if (suggestions.length === 0) {
            generateSuggestions(tracks);
          }
        }
      }
    } catch (error) {
      console.error('❌ Failed to load playlist:', error);
      toast({
        title: "Error",
        description: "Failed to load your selections",
        variant: "destructive"
      });
    }
  };
  const generateInitialSuggestions = async (specificEventContext: any) => {
    // AI suggestions removed - users should search manually
    setIsLoadingSuggestions(false);
    setSuggestions([]);
    toast({
      title: "Manual search available",
      description: "Use the search button to find songs for your playlist",
    });
  };
  const generateSuggestions = async (currentPlaylist: Track[]) => {
    // AI suggestions removed - users should search manually
    if (currentPlaylist.length === 0) return;
    setIsLoadingSuggestions(false);
    setSuggestions([]);
  };
  const handleAddToPlaylist = useCallback(async (song: Track, manuallyAdded: boolean = true) => {
    // Check maximum limit of 25 songs
    if (playlist.length >= 25) {
      toast({
        title: "Taste Profile Full",
        description: "Maximum 25 songs allowed. Remove songs to add more.",
        variant: "destructive"
      });
      return;
    }

    // Check for duplicates in local state
    const isDuplicate = playlist.some(track => track.id === song.id);
    if (isDuplicate) {
      toast({
        title: "Already in Your Selections",
        description: `"${song.name}" is already in your playlist`,
        variant: "default"
      });
      return;
    }

    // Check for duplicates in database
    try {
      const {
        data: existing,
        error: checkError
      } = await supabase.from('favorite_songs').select('id').eq('session_id', sessionId).eq('spotify_uri', song.uri).eq('event_index', selectedEventIndex).maybeSingle();
      if (checkError) {
        console.error('❌ Error checking for duplicates:', checkError);
        throw checkError;
      }
      if (existing) {
        toast({
          title: "Already in Your Selections",
          description: `"${song.name}" is already in your playlist`,
          variant: "default"
        });
        return;
      }
    } catch (error) {
      console.error('❌ Error checking for duplicates:', error);
      toast({
        title: "Error",
        description: "Failed to check for duplicates. Please try again.",
        variant: "destructive"
      });
      return;
    }

    // Calculate context weight based on event context and source
    const specificEventContext = eventContext?.events?.[selectedEventIndex] || eventContext;
    const baseWeight = manuallyAdded ? 10.0 : 5.0; // Manual additions get max weight

    // Add to database with tracking
    try {
      console.log('💾 Saving song to database:', {
        sessionId,
        songTitle: song.name,
        eventIndex: selectedEventIndex,
        hasPreview: !!song.previewUrl,
        spotifyUri: song.uri
      });

      const insertData = {
        session_id: sessionId,
        song_title: song.name,
        artist: song.artist,
        album: song.album,
        image_url: song.imageUrl,
        preview_url: song.previewUrl,
        spotify_uri: song.uri,
        event_index: selectedEventIndex,
        duration_ms: song.durationMs || 0,
        manually_added: manuallyAdded,
        context_weight: baseWeight
      };

      console.log('📝 Insert data:', JSON.stringify(insertData, null, 2));

      const {
        error
      } = await supabase.from('favorite_songs').insert(insertData);
      
      if (error) {
        console.error('❌ Database insert error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        // Check if it's a duplicate error from the unique constraint
        if (error.code === '23505') {
          toast({
            title: "Already in Your Selections",
            description: `"${song.name}" is already in your playlist`,
            variant: "default"
          });
          return;
        }
        // Check for RLS policy violation
        if (error.message?.includes('row-level security') || error.message?.includes('policy')) {
          console.error('🚨 RLS POLICY VIOLATION - Session ID might not be matching');
          toast({
            title: "Permission Error",
            description: "Unable to save song. Please refresh the page and try again.",
            variant: "destructive"
          });
          return;
        }
        throw error;
      }
      
      console.log('✅ Song saved successfully:', song.name);
      
      // Immediately reload the playlist to update UI
      await loadPlaylist();
      
      toast({
        title: "Added to Your Selections",
        description: `"${song.name}" by ${song.artist}`,
      });
    } catch (error) {
      console.error('❌ Error saving song:', error);
      toast({
        title: "Error",
        description: "Failed to save song. Please try again.",
        variant: "destructive"
      });
      return;
    }

    // Local state will be updated by real-time subscription

    // Call parent handler with event index
    onAddSong(song, selectedEventIndex);

    // Auto-play the song if it has a preview
    if (song.previewUrl && audioConsented) {
      console.log('🎵 Auto-playing song:', song.name, 'Preview URL:', song.previewUrl);
      setTimeout(() => {
        onPlayToggle(song);
      }, 300);
    } else {
      console.log('⚠️ No preview URL for song:', song.name, 'Will use YouTube fallback');
    }
  }, [playlist, sessionId, selectedEventIndex, eventContext, toast, onAddSong, audioConsented, onPlayToggle]);
  const handleRemoveSong = async (songId: string) => {
    try {
      // Delete by song ID - the songId could be just the ID or include the full URI
      const spotifyUri = songId.startsWith('spotify:track:') ? songId : `spotify:track:${songId}`;
      
      const {
        error
      } = await supabase
        .from('favorite_songs')
        .delete()
        .eq('session_id', sessionId)
        .eq('event_index', selectedEventIndex)
        .eq('spotify_uri', spotifyUri);
      
      if (error) throw error;

      // Real-time will handle the update automatically
    } catch (error) {
      console.error('Error removing song:', error);
      toast({
        title: "Error",
        description: "Failed to remove song",
        variant: "destructive"
      });
    }
  };
  const handleClearAll = async () => {
    // First click - show confirmation
    if (!showClearConfirm) {
      setShowClearConfirm(true);
      // Auto-reset after 3 seconds if user doesn't confirm
      setTimeout(() => {
        setShowClearConfirm(false);
      }, 3000);
      return;
    }

    // Second click - actually clear
    try {
      // Delete all songs from favorite_songs
      const {
        error: songsError
      } = await supabase.from('favorite_songs').delete().eq('session_id', sessionId).eq('event_index', selectedEventIndex);
      if (songsError) throw songsError;

      // Reset all local state
      setPlaylist([]);
      setDetectedVibes([]);
      setSelectedVibes([]);
      setGenreBlend(null);
      setSuggestions([]);
      setShowClearConfirm(false);
      setRecentlyAppliedVibes([]);

      // Notify parent to reset vibes if callback exists
      if (onVibesUpdate) {
        onVibesUpdate([]);
      }

      // Real-time will handle the update automatically
    } catch (error) {
      console.error('Error clearing playlist:', error);
      setShowClearConfirm(false);
      toast({
        title: "Error",
        description: "Failed to clear playlist",
        variant: "destructive"
      });
    }
  };

  // Generate a random string for PKCE
  const generateRandomString = (length: number) => {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const values = crypto.getRandomValues(new Uint8Array(length));
    return Array.from(values).map(x => possible[x % possible.length]).join('');
  };

  // Generate code challenge from verifier
  const generateCodeChallenge = async (codeVerifier: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  };
  const handleConnectSpotify = async () => {
    try {
      console.log('🎵 [OAuth] Initiating Spotify connection');
      const redirectUri = `${window.location.origin}/spotify-callback`;
      console.log('🎵 [OAuth] Redirect URI:', redirectUri);

      // Generate PKCE values
      const codeVerifier = generateRandomString(64);
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateRandomString(16);
      console.log('🎵 [OAuth] Generated state:', state);

      // Store PKCE values in sessionStorage AND localStorage as backup
      sessionStorage.setItem('spotify_code_verifier', codeVerifier);
      sessionStorage.setItem('spotify_state', state);
      localStorage.setItem('spotify_code_verifier', codeVerifier);
      localStorage.setItem('spotify_state', state);
      localStorage.setItem('spotify_auth_timestamp', Date.now().toString());
      console.log('🎵 [OAuth] Stored PKCE values in session and local storage');

      // Store return URL and current page state for after OAuth
      localStorage.setItem('spotify_return_url', '/vibe-builder');
      console.log('🎵 [OAuth] Set return URL to /vibe-builder');

      // Persist session ID to localStorage to survive cross-tab redirect
      if (sessionId) {
        localStorage.setItem('session_id', sessionId);
        console.log('🎵 [OAuth] Stored session ID:', sessionId);
      }

      // Persist OAuth state on backend (for cross-domain/tab reliability)
      console.log('🎵 [OAuth] Saving OAuth state to backend, state:', state);
      const saveState = await supabase.functions.invoke('spotify-oauth-state', {
        body: {
          op: 'save',
          state,
          codeVerifier,
          sessionId,
          redirectUri
        }
      });
      if (saveState.error) {
        console.error('❌ [OAuth] Failed to save state to backend:', saveState.error);
        throw saveState.error;
      }
      console.log('✅ [OAuth] OAuth state saved successfully');

      // Get authorization URL from backend
      console.log('🎵 [OAuth] Requesting authorization URL from backend');
      const {
        data,
        error
      } = await supabase.functions.invoke('spotify-auth-url', {
        body: {
          redirectUri,
          codeChallenge,
          state
        }
      });
      if (error) {
        console.error('❌ [OAuth] Error getting auth URL:', error);
        throw error;
      }
      if (!data?.authUrl) {
        console.error('❌ [OAuth] No auth URL received from backend');
        throw new Error('Failed to get authorization URL');
      }
      console.log('✅ [OAuth] Received authorization URL, redirecting to Spotify...');
      // Redirect to Spotify authorization - open in new tab when inside preview iframe
      if (window.top && window.top !== window.self) {
        console.log('🎵 [OAuth] Opening in new tab (iframe detected)');
        window.open(data.authUrl, '_blank', 'noopener,noreferrer');
      } else {
        console.log('🎵 [OAuth] Redirecting in same window');
        window.location.href = data.authUrl;
      }
    } catch (error) {
      console.error('❌ [OAuth] Error connecting to Spotify:', error);
      toast({
        title: "Connection Failed",
        description: "Failed to start Spotify authorization",
        variant: "destructive"
      });
    }
  };
  const handleImportPlaylist = async () => {
    const url = playlistUrl.trim();
    if (!url) {
      toast({
        title: "URL Required",
        description: "Please paste a Spotify playlist URL",
        variant: "destructive"
      });
      return;
    }
    const isPlaylistUrl = /open\.spotify\.com\/playlist\/[A-Za-z0-9]+/.test(url) || /spotify:playlist:[A-Za-z0-9]+/.test(url);
    if (!isPlaylistUrl) {
      toast({
        title: "Invalid URL",
        description: "Please paste a valid Spotify playlist URL (albums and artists are not supported)",
        variant: "destructive"
      });
      return;
    }

    // Extract playlist ID to check for duplicates
    const playlistIdMatch = url.match(/playlist\/([A-Za-z0-9]+)/);
    const playlistId = playlistIdMatch ? playlistIdMatch[1] : url.split('spotify:playlist:')[1];
    
    // Check if this exact playlist was already imported
    if (playlistId) {
      try {
        const { data: existingSongs, error } = await supabase
          .from('favorite_songs')
          .select('id')
          .eq('session_id', sessionId)
          .eq('event_index', selectedEventIndex)
          .eq('manually_added', false)
          .limit(1);
        
        if (error) throw error;
        
        // Check if we have songs marked as imported (from playlist)
        if (existingSongs && existingSongs.length > 0) {
          // Check local state for this specific playlist ID
          if (importedPlaylistIds.has(playlistId)) {
            toast({
              title: "Already Imported",
              description: "This playlist has already been imported for this event",
              variant: "default"
            });
            return;
          }
        }
      } catch (error) {
        console.error('Error checking for duplicate imports:', error);
      }
    }

    // Ensure sessionId is valid
    if (!sessionId) {
      toast({
        title: "Session Error",
        description: "Session ID not found. Please refresh the page and try again.",
        variant: "destructive"
      });
      return;
    }
    setIsImportingPlaylist(true);
    setImportProgress(10);
    
    // Check maximum limit of 25 songs
    const MAX_SONGS = 25;
    const remainingCapacity = MAX_SONGS - playlist.length;
    
    if (remainingCapacity <= 0) {
      toast({
        title: "Taste Profile Full",
        description: "Maximum 25 songs reached. Remove songs to import more.",
        variant: "destructive"
      });
      setIsImportingPlaylist(false);
      return;
    }
    
    // Store initial playlist count to detect when new songs arrive
    const initialPlaylistCount = playlist.length;
    
    try {
      console.log('📥 Starting playlist import:', {
        url: playlistUrl.trim(),
        sessionId,
        eventIndex: selectedEventIndex,
        currentSongs: playlist.length,
        remainingCapacity
      });
      
      setImportProgress(30);
      const {
        data,
        error
      } = await supabase.functions.invoke('import-spotify-playlist', {
        body: {
          playlistUrl: playlistUrl.trim(),
          sessionId: sessionId
        }
      });
      
      console.log('🔌 [PlaylistBuilder] import-spotify-playlist response:', { data, error });
      
      setImportProgress(50);
      if (error) {
        console.error('❌ [PlaylistBuilder] Import invoke error object:', error);
        console.error('❌ [PlaylistBuilder] Import invoke error JSON:', JSON.stringify(error, null, 2));
        throw error;
      }
      if (!data?.tracks || data.tracks.length === 0) {
        toast({
          title: "No Tracks Found",
          description: "This playlist appears to be empty",
          variant: "destructive"
        });
        return;
      }

      console.log(`✅ Retrieved ${data.tracks.length} tracks from playlist`);

      setImportProgress(60);

      // Limit tracks to remaining capacity
      const tracksToImport = data.tracks.slice(0, remainingCapacity);
      const skippedCount = data.tracks.length - tracksToImport.length;
      
      if (skippedCount > 0) {
        console.log(`Limiting import to ${tracksToImport.length} songs (${skippedCount} skipped due to 25 song limit)`);
      }

      // Pre-buffer audio previews for tracks that have them
      const tracksWithPreviews = tracksToImport.filter((track: any) => track.preview_url || track.previewUrl);
      if (tracksWithPreviews.length > 0 && audioConsented) {
        console.log(`Pre-buffering ${tracksWithPreviews.length} audio previews...`);
        // Pre-load first 3 tracks' audio for instant playback
        const preloadPromises = tracksWithPreviews.slice(0, 3).map((track: any) => {
          return new Promise((resolve) => {
            const previewUrl = track.preview_url || track.previewUrl;
            if (!previewUrl) {
              resolve(false);
              return;
            }
            const audio = new Audio(previewUrl);
            audio.preload = 'auto';
            audio.addEventListener('canplaythrough', () => resolve(true), { once: true });
            audio.addEventListener('error', () => resolve(false), { once: true });
            // Timeout after 2 seconds
            setTimeout(() => resolve(false), 2000);
          });
        });
        await Promise.race([
          Promise.all(preloadPromises),
          new Promise(resolve => setTimeout(resolve, 3000)) // Max 3s wait
        ]);
      }

      setImportProgress(70);

      // Add limited tracks to database (marked as imported, not manually added)
      const tracksToAdd = tracksToImport.map((track: any) => ({
        session_id: sessionId,
        song_title: track.name,
        artist: track.artist,
        album: track.album,
        image_url: track.image_url || track.imageUrl, // Support both formats
        preview_url: track.preview_url || track.previewUrl,
        spotify_uri: track.spotify_uri || track.uri,
        event_index: selectedEventIndex,
        duration_ms: track.duration_ms || track.durationMs || 0,
        manually_added: false,
        context_weight: 5.0 // Default weight for imported songs
      }));
      
      setImportProgress(80);
      console.log(`💾 Inserting ${tracksToAdd.length} tracks into database...`);
      const { error: insertError } = await supabase.from('favorite_songs').upsert(tracksToAdd, {
        onConflict: 'session_id,spotify_uri,event_index',
        ignoreDuplicates: true
      });
      if (insertError) {
        console.error('❌ Insert error:', insertError);
        // Check if it's a duplicate error (shouldn't happen with ignoreDuplicates)
        if (insertError.code === '23505' || insertError.code === '409') {
          toast({
            title: "Playlist Already Imported",
            description: "Some or all of these songs are already in your profile",
            variant: "default"
          });
          return;
        }
        throw insertError;
      }

      console.log('✅ Tracks inserted successfully');

      setImportProgress(85);

      // Immediately reload the playlist to update UI
      await loadPlaylist();

      // Wait for real-time subscription to update the playlist UI
      // Poll for new songs to appear (with timeout)
      let attempts = 0;
      const maxAttempts = 10;
      const checkInterval = 300; // 300ms between checks
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, checkInterval));
        
        // Check if new songs have been added to local state via real-time
        if (playlist.length > initialPlaylistCount) {
          console.log('Songs loaded in UI via real-time subscription');
          break;
        }
        attempts++;
      }
      
      // Track this playlist as imported
      if (playlistId) {
        setImportedPlaylistIds(prev => new Set(prev).add(playlistId));
      }

      setImportProgress(95);
      
      // Generate suggestions and detect vibes after songs are in state
      if (playlist.length > initialPlaylistCount) {
        const updatedPlaylist = [...playlist];
        generateSuggestions(updatedPlaylist);
        detectPlaylistVibes(updatedPlaylist);
      }
      
      setImportProgress(100);
      
      // Show success message with info about skipped songs if applicable
      if (skippedCount > 0) {
        toast({
          title: "Playlist Imported!",
          description: `Added ${tracksToImport.length} song${tracksToImport.length !== 1 ? 's' : ''}. ${skippedCount} song${skippedCount !== 1 ? 's were' : ' was'} skipped (25 song limit reached).`
        });
      } else {
        toast({
          title: "Playlist Imported!",
          description: `Added ${tracksToImport.length} song${tracksToImport.length !== 1 ? 's' : ''} to your taste profile`
        });
      }
      setPlaylistUrl("");
    } catch (err: unknown) {
      console.error('❌ [PlaylistBuilder] Error importing playlist:', err);
      let details = '';
      let needsAuth = false;
      const anyErr = err as any;
      if (anyErr?.context?.body) {
        try {
          const parsed = JSON.parse(anyErr.context.body);
          details = parsed?.error ?? '';
          needsAuth = parsed?.needsAuth ?? false;
        } catch {}
      }

      // If needs auth, clear connection status and prompt to reconnect
      if (needsAuth) {
        sessionStorage.removeItem('spotify_connected');
        sessionStorage.removeItem('spotify_expires_at');
        setIsSpotifyConnected(false);
        toast({
          title: "Spotify Connection Required",
          description: "Please connect your Spotify account to import playlists",
          variant: "destructive"
        });
      } else {
        const message = err instanceof Error 
          ? err.message 
          : typeof err === 'string' 
          ? err 
          : details || 'Unknown error while importing playlist';
        
        toast({
          title: "Import Failed",
          description: message.includes('Failed to send') 
            ? 'Network or backend function connectivity issue. Please try again.' 
            : message,
          variant: "destructive"
        });
      }
    } finally {
      setIsImportingPlaylist(false);
      setTimeout(() => setImportProgress(0), 500);
    }
  };
  const vibeOptions = [{
    id: 'upbeat',
    label: 'Upbeat',
    icon: Zap,
    color: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300 border-yellow-500/40'
  }, {
    id: 'chill',
    label: 'Chill',
    icon: Heart,
    color: 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/40'
  }, {
    id: 'nostalgic',
    label: 'Nostalgic',
    icon: Radio,
    color: 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/40'
  }, {
    id: 'bollywood',
    label: 'Bollywood',
    icon: Globe,
    color: 'bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-500/40'
  }, {
    id: 'party',
    label: 'Party',
    icon: Sparkles,
    color: 'bg-pink-500/20 text-pink-700 dark:text-pink-300 border-pink-500/40'
  }, {
    id: 'romantic',
    label: 'Romantic',
    icon: Heart,
    color: 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/40'
  }];
  const toggleVibe = (vibeId: string) => {
    setSelectedVibes(prev => {
      const newVibes = prev.includes(vibeId) ? prev.filter(v => v !== vibeId) : [...prev, vibeId];

      // Notify parent component if callback provided
      if (onVibesUpdate) {
        onVibesUpdate(newVibes);
      }

      // Light haptic for selection
      if ('vibrate' in navigator) {
        navigator.vibrate(10);
      }
      return newVibes;
    });
  };

  // No longer needed - vibes auto-apply
  // Keeping for backward compatibility but it's now redundant
  const applyDetectedVibes = () => {
    setSelectedVibes(detectedVibes);
    setRecentlyAppliedVibes(detectedVibes);

    // Haptic feedback on mobile
    if ('vibrate' in navigator) {
      navigator.vibrate([10, 20, 10]); // Short-long-short pattern
    }

    // Clear the animation state after animation completes
    setTimeout(() => {
      setRecentlyAppliedVibes([]);
    }, 800);
    toast({
      title: "Vibes applied!",
      description: `Using ${detectedVibes.length} detected vibe${detectedVibes.length !== 1 ? 's' : ''} for recommendations`
    });
  };
  return <>
      
      <div className="space-y-4">
      {/* Event Selector - Only show if multiple events */}
      {eventContext?.events && eventContext.events.length > 1}
      
      {/* Playlist Header */}
      <Card className={`p-4 sm:p-5 relative overflow-hidden transition-all duration-500 ${shouldGlow ? 'ring-2 ring-primary/50 shadow-lg' : ''}`}>
        
        {shouldGlow && (
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-lg pointer-events-none" 
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
        
        <div className="relative z-10 space-y-4">
          {/* Header Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-primary/10">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold flex items-center gap-1.5">
                  {eventContext?.events && eventContext.events.length > 1 
                    ? `Event ${selectedEventIndex + 1} Songs` 
                    : 'Your Taste Profile'}
                  <span className="text-destructive text-xs">*</span>
                </h3>
                <p className="text-xs text-muted-foreground">
                  {playlist.length}/25 songs • {formatDuration(totalDuration)}
                </p>
              </div>
            </div>
            
            {/* Progress indicator */}
            {playlist.length > 0 && (
              <Badge 
                variant={isTargetReached ? "default" : "secondary"} 
                className={`text-xs ${isTargetReached ? 'bg-primary/20 text-primary border-primary/30' : ''}`}
              >
                {isTargetReached ? '✓ Ready' : `${playlist.length}/5 min`}
              </Badge>
            )}
          </div>

          {/* Status message - only show when needed */}
          {selectedGenres.length < 3 && (
            <p className="text-xs text-destructive bg-destructive/10 rounded-md px-3 py-2">
              Select at least 3 genres above to start adding songs
            </p>
          )}
          
          {/* Search & Import Section */}
          <div className={`space-y-3 ${selectedGenres.length < 3 ? 'opacity-40 pointer-events-none' : ''}`}>
            <SpotifySearch 
              onAddSong={handleAddToPlaylist} 
              shouldPulse={playlist.length === 0 && shouldGlow} 
              playingSongId={playingSongId} 
              onPlayToggle={onPlayToggle} 
              audioConsented={audioConsented} 
              disabled={playlist.length >= 25} 
            />
            
            {/* Collapsible Import Section */}
            <details className="group">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors flex items-center gap-1">
                <Music className="h-3 w-3" />
                Import from Spotify playlist
              </summary>
              <div className="mt-2 p-3 bg-muted/30 rounded-lg border border-border/30">
                <div className="flex gap-2">
                  <Input 
                    placeholder={playlist.length >= 25 ? "Maximum reached" : "Paste Spotify playlist URL"} 
                    value={playlistUrl} 
                    onChange={e => setPlaylistUrl(e.target.value)} 
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !isImportingPlaylist && playlist.length < 25 && selectedGenres.length >= 3) {
                        handleImportPlaylist();
                      }
                    }} 
                    disabled={isImportingPlaylist || playlist.length >= 25} 
                    className="h-8 text-xs" 
                  />
                  <Button 
                    onClick={handleImportPlaylist} 
                    disabled={isImportingPlaylist || !playlistUrl.trim() || playlist.length >= 25} 
                    size="sm" 
                    variant="secondary"
                    className="h-8 text-xs px-3"
                  >
                    {isImportingPlaylist ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Import'}
                  </Button>
                </div>
                
                {isImportingPlaylist && importProgress > 0 && (
                  <div className="mt-2">
                    <Progress value={importProgress} className="h-1" />
                  </div>
                )}
              </div>
            </details>
          </div>

          {/* Progress bar - compact version */}
          {playlist.length > 0 && !isTargetReached && (
            <div className="pt-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>Progress to minimum</span>
                <span>{playlist.length}/5 songs</span>
              </div>
              <Progress value={progressPercent} className="h-1.5" />
            </div>
          )}

          {/* Empty state */}
          {playlist.length === 0 && selectedGenres.length >= 3 && (
            <div className="text-center py-6 text-muted-foreground">
              <Music className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Search for songs above to build your taste profile</p>
            </div>
          )}

          {/* Mini Audio Player - Above Your Selections */}
          {currentTrack && (
            <MiniAudioPlayer
              trackName={currentTrack.name}
              artistName={currentTrack.artist}
              imageUrl={currentTrack.imageUrl}
              progress={progress}
              duration={duration}
              currentTime={currentTime}
              source={currentTrack.youtubeId ? 'youtube' : 'spotify'}
              isPlaying={isPlaying}
              onStop={stopPlayback}
              onTogglePlayPause={togglePlayPause}
            />
          )}

          {/* Current Playlist */}
          {playlist.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-border/30">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Your Selections</span>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={handleClearAll} 
                  className={`h-6 text-xs px-2 ${showClearConfirm ? 'text-destructive' : 'text-muted-foreground hover:text-destructive'}`}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  {showClearConfirm ? "Confirm?" : "Clear"}
                </Button>
              </div>
              
              <div className="grid gap-1.5 max-h-48 overflow-y-auto overflow-x-hidden">
                <AnimatePresence mode="popLayout">
                  {playlist.map((track, index) => (
                    <motion.div 
                      key={`${track.id}-${index}`} 
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      layout
                    >
                      <div className="flex items-center gap-2 p-1.5 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors group">
                        <img 
                          src={track.imageUrl} 
                          alt={track.name} 
                          className="w-8 h-8 rounded object-cover flex-shrink-0" 
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{track.name}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{track.artist}</p>
                        </div>
                        <div className="flex items-center flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            onClick={() => onPlayToggle(track)} 
                            className="h-6 w-6"
                          >
                            {playingSongId === track.id && isPlaying ? (
                              <Pause className="h-3 w-3" />
                            ) : (
                              <Play className="h-3 w-3" />
                            )}
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            onClick={() => handleRemoveSong(track.id)} 
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Manual Vibe Selection removed */}
    </div>
  </>;
};